import { S3Service } from '../../../drive/s3Service';
import type { H2ODriveConfig, S3Object } from '../../../drive/types';

export interface CrossWorkspaceConfig {
  workspaceId: string;
  stsDriveUrl: string;
  workspaceDriveUrl: string;
}

const normalizeDestinationKey = (key: string): string => {
  return key.endsWith('/') ? key : `${key}/`;
};

const createTempS3Service = async ({
  workspaceId,
  stsDriveUrl,
  workspaceDriveUrl,
}: CrossWorkspaceConfig): Promise<S3Service> => {
  const config: H2ODriveConfig = {
    driveStsUrl: stsDriveUrl,
    driveS3Url: workspaceDriveUrl,
    workspaceId,
    roleArn: 'drivedrivedrivedrive',
  };

  const tempS3Service = new S3Service();
  await tempS3Service.initialize(config);
  return tempS3Service;
};

export const listObjectsFromWorkspace = async (config: CrossWorkspaceConfig): Promise<S3Object[]> => {
  try {
    const tempS3Service = await createTempS3Service(config);
    return await tempS3Service.listAllObjects();
  } catch (error) {
    console.error(`Failed to list objects from workspace ${config.workspaceId}:`, error);
    throw error;
  }
};

export const createDirectoryInWorkspace = async (
  config: CrossWorkspaceConfig,
  directoryName: string,
  prefix: string
): Promise<void> => {
  try {
    const tempS3Service = await createTempS3Service(config);
    await tempS3Service.createDirectory(directoryName, prefix);
  } catch (error) {
    console.error(`Failed to create directory in workspace ${config.workspaceId}:`, error);
    throw error;
  }
};

export const copyFileAcrossWorkspaces = async (
  sourceConfig: CrossWorkspaceConfig,
  destinationConfig: CrossWorkspaceConfig,
  sourceKey: string,
  destinationKey: string
): Promise<void> => {
  try {
    // Step 1: Download from source workspace
    const sourceS3Service = await createTempS3Service(sourceConfig);
    const fileBlob = await sourceS3Service.downloadFile(sourceKey);

    // Convert Blob to File for upload
    const fileName = sourceKey.split('/').pop() || 'file';
    const file = new File([fileBlob], fileName, { type: fileBlob.type });

    // Step 2: Upload to destination workspace
    const destS3Service = await createTempS3Service(destinationConfig);
    await destS3Service.uploadFile(file, destinationKey);
  } catch (error) {
    console.error(`Failed to copy file from ${sourceConfig.workspaceId} to ${destinationConfig.workspaceId}:`, error);
    throw error;
  }
};

export const moveFileAcrossWorkspaces = async (
  sourceConfig: CrossWorkspaceConfig,
  destinationConfig: CrossWorkspaceConfig,
  sourceKey: string,
  destinationKey: string
): Promise<void> => {
  try {
    // Step 1: Copy the file
    await copyFileAcrossWorkspaces(sourceConfig, destinationConfig, sourceKey, destinationKey);

    // Step 2: Delete from source workspace
    const sourceS3Service = await createTempS3Service(sourceConfig);
    await sourceS3Service.deleteFile(sourceKey);
  } catch (error) {
    console.error(`Failed to move file from ${sourceConfig.workspaceId} to ${destinationConfig.workspaceId}:`, error);
    throw error;
  }
};

export const copyDirectoryAcrossWorkspaces = async (
  sourceConfig: CrossWorkspaceConfig,
  destinationConfig: CrossWorkspaceConfig,
  sourceKey: string,
  destinationKey: string
): Promise<void> => {
  try {
    // Step 1: List all objects in the source directory
    const sourceS3Service = await createTempS3Service(sourceConfig);
    const allObjects = await sourceS3Service.listAllObjects();

    // Filter objects that are under the source directory
    const sourcePrefix = sourceKey.endsWith('/') ? sourceKey : `${sourceKey}/`;
    const objectsToCopy = allObjects.filter((obj) => obj.Key && obj.Key.startsWith(sourcePrefix));

    if (objectsToCopy.length === 0) {
      // If no objects found, just create the directory marker
      const destS3Service = await createTempS3Service(destinationConfig);
      await destS3Service.createDirectory(destinationKey, '');
      return;
    }

    // Step 2: Copy each file/directory
    const normalizedDestKey = normalizeDestinationKey(destinationKey);

    for (const obj of objectsToCopy) {
      if (!obj.Key) continue;

      const relativePath = obj.Key.substring(sourcePrefix.length);
      const newKey = normalizedDestKey + relativePath;

      if (obj.Key.endsWith('/')) {
        // Directory marker - recreate in destination
        const destS3Service = await createTempS3Service(destinationConfig);
        const dirName = relativePath.slice(0, -1);
        const parentPath =
          normalizedDestKey + relativePath.substring(0, relativePath.lastIndexOf('/', relativePath.length - 2) + 1);
        await destS3Service.createDirectory(dirName, parentPath);
      } else {
        // Regular file - copy to destination
        await copyFileAcrossWorkspaces(sourceConfig, destinationConfig, obj.Key, newKey);
      }
    }
  } catch (error) {
    console.error(
      `Failed to copy directory from ${sourceConfig.workspaceId} to ${destinationConfig.workspaceId}:`,
      error
    );
    throw error;
  }
};

export const moveDirectoryAcrossWorkspaces = async (
  sourceConfig: CrossWorkspaceConfig,
  destinationConfig: CrossWorkspaceConfig,
  sourceKey: string,
  destinationKey: string
): Promise<void> => {
  try {
    // Step 1: Copy the entire directory
    await copyDirectoryAcrossWorkspaces(sourceConfig, destinationConfig, sourceKey, destinationKey);

    // Step 2: Delete the source directory
    const sourceS3Service = await createTempS3Service(sourceConfig);
    await sourceS3Service.deleteDirectory(sourceKey);
  } catch (error) {
    console.error(
      `Failed to move directory from ${sourceConfig.workspaceId} to ${destinationConfig.workspaceId}:`,
      error
    );
    throw error;
  }
};
